import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PatientService from "../services/PatientService";
import BloodTypeService from "../services/BloodTypeService";

function PatientList() {
  const [search, setSearch] = useState("");
  const [patients, setPatients] = useState([]);
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [bloodTypes, setBloodTypes] = useState([]);
  const [newPatient, setNewPatient] = useState({
    name: "",
    surname: "",
    email: "",
    phone: "",
    height: "",
    birthDate: "",
    lbo: "",
    medicalNote: "",
    allergies: "",
    surgeries: "",
    otherInfo: "",
    bloodTypeID: ""
  });

  const navigate = useNavigate();

  useEffect(() => {
    loadPatients();
    loadBloodTypes();
  }, []);

  const loadPatients = () => {
    PatientService.getAll()
      .then((data) => setPatients(data || []))
      .catch((err) => console.error("Greška pri učitavanju pacijenata:", err));
  };

  const loadBloodTypes = () => {
    BloodTypeService.getAllBloodTypes()
      .then((data) => setBloodTypes(data || []))
      .catch((err) => console.error("Greška pri učitavanju krvnih grupa:", err));
  };

  const handleAddChange = (e) => {
    const { name, value } = e.target;
    setNewPatient({ ...newPatient, [name]: value });
  };

  const handleAddSubmit = (e) => {
    e.preventDefault();
    if (!newPatient.bloodTypeID) {
      alert("Morate izabrati krvnu grupu!");
      return;
    }
    const payload = {
      ...newPatient,
      height: newPatient.height ? parseFloat(newPatient.height) : null,
      bloodType: { bloodID: parseInt(newPatient.bloodTypeID) }
    };

    PatientService.create(payload)
      .then(() => {
        setShowAddPopup(false);
        setNewPatient({
          name: "", surname: "", email: "", phone: "", height: "",
          birthDate: "", lbo: "", medicalNote: "", 
          allergies: "", surgeries: "", otherInfo: "", bloodTypeID: ""
        });
        loadPatients();
      })
      .catch((err) => {
        console.error("Greška pri kreiranju:", err);
        alert("Greška pri kreiranju pacijenta.");
      });
  };

  const highlightText = (text) => {
    if (!search || !text) return text;
    const regex = new RegExp(`(${search})`, "gi");
    return text.toString().split(regex).map((part, i) =>
      regex.test(part) ? <span key={i} style={{ backgroundColor: "#ffeb3b" }}>{part}</span> : part
    );
  };

  const formatDate = (isoDate) => {
    if (!isoDate) return "/";
    const d = new Date(isoDate);
    return d.toLocaleDateString("sr-RS");
  };

  const exportToCSV = () => {
    if (patients.length === 0) {
      alert("Nema pacijenata za eksport.");
      return;
    }
    const headers = ["Ime", "Prezime", "Telefon", "Datum rođenja", "Alergije"];
    const rows = patients.map(p => [
      `"${p.name || ""}"`, `"${p.surname || ""}"`, `"${p.phone || ""}"`,
      `"${formatDate(p.birthDate)}"`, `"${p.allergies || ""}"`
    ]);
    const csvContent = "\uFEFF" + [headers, ...rows].map(e => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "pacijenti.csv");
    link.click();
  };

  return (
    <div style={pageContainer}>
      <div style={headerStyle}>
        <h2 style={{ color: "#2c3e50", margin: 0 }}>📋 Lista Zdravstvenih Kartona</h2>
        <div style={{ display: "flex", gap: "10px" }}>
          <button onClick={() => setShowAddPopup(true)} style={greenButton}>➕ Dodaj Pacijenta</button>
          <button onClick={exportToCSV} style={blueButton}>📥 Izvezi CSV</button>
        </div>
      </div>

      <input
        type="text"
        placeholder="Pretraži po imenu, prezimenu ili telefonu..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={searchInput}
      />

      <div style={tableWrapper}>
        <table style={tableStyle}>
          <thead>
            <tr style={thRowStyle}>
              <th style={thStyle}>Ime</th>
              <th style={thStyle}>Prezime</th>
              <th style={thStyle}>Telefon</th>
              <th style={thStyle}>Datum rođenja</th>
              <th style={thStyle}>Akcije</th>
            </tr>
          </thead>
          <tbody>
            {patients
              .filter(p => 
                  p.name?.toLowerCase().includes(search.toLowerCase()) || 
                  p.surname?.toLowerCase().includes(search.toLowerCase()) || 
                  p.phone?.includes(search)
              )
              .map((p, idx) => (
                <tr key={p.patientID} style={idx % 2 === 0 ? trEven : trOdd}>
                  <td style={tdStyle}>{highlightText(p.name)}</td>
                  <td style={tdStyle}>{highlightText(p.surname)}</td>
                  <td style={tdStyle}>{p.phone || "/"}</td>
                  <td style={tdStyle}>{formatDate(p.birthDate)}</td>
                  <td style={tdStyle}>
                    <button 
                      onClick={() => navigate(`/patient-carton/${p.patientID}`)} 
                      style={actionButton}
                    >
                      📂 Otvori Karton
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* MODAL ZA DODAVANJE NOVOG PACIJENTA */}
      {showAddPopup && (
        <div style={modalOverlay}>
          <div style={modalContent}>
            <h3 style={{ borderBottom: "2px solid #eee", paddingBottom: "10px" }}>Novi Pacijent</h3>
            <form onSubmit={handleAddSubmit} style={formStyle}>
              <div style={inputGroup}>
                <input name="name" placeholder="Ime" onChange={handleAddChange} style={inputStyle} required />
                <input name="surname" placeholder="Prezime" onChange={handleAddChange} style={inputStyle} required />
              </div>
              <input name="email" type="email" placeholder="Email adresa" onChange={handleAddChange} style={inputStyle} />
              <input name="phone" placeholder="Telefon" onChange={handleAddChange} style={inputStyle} />
              <div style={inputGroup}>
                <input name="birthDate" type="date" onChange={handleAddChange} style={inputStyle} />
                <input name="lbo" placeholder="LBO" onChange={handleAddChange} style={inputStyle} />
              </div>
              
              <select 
                value={newPatient.bloodTypeID} 
                onChange={(e) => setNewPatient({ ...newPatient, bloodTypeID: e.target.value })} 
                style={inputStyle} 
                required
              >
                <option value="">-- Izaberi krvnu grupu --</option>
                {bloodTypes.map(b => <option key={b.bloodID} value={b.bloodID}>{b.bloodTypeName}</option>)}
              </select>

              <textarea name="allergies" placeholder="Alergije..." onChange={handleAddChange} style={textAreaStyle} />
              <textarea name="medicalNote" placeholder="Glavna napomena..." onChange={handleAddChange} style={textAreaStyle} />

              <div style={buttonGroup}>
                <button type="submit" style={greenButton}>Sačuvaj</button>
                <button type="button" onClick={() => setShowAddPopup(false)} style={redButton}>Odustani</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// --- STILOVI ---
const pageContainer = { padding: "40px", backgroundColor: "#f4f7f6", minHeight: "100vh", fontFamily: "'Segoe UI', sans-serif" };
const headerStyle = { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "30px" };
const searchInput = { width: "100%", padding: "15px", borderRadius: "10px", border: "1px solid #ddd", marginBottom: "25px", fontSize: "16px", boxShadow: "0 2px 5px rgba(0,0,0,0.05)" };
const tableWrapper = { backgroundColor: "#fff", borderRadius: "12px", boxShadow: "0 4px 15px rgba(0,0,0,0.1)", overflow: "hidden" };
const tableStyle = { width: "100%", borderCollapse: "collapse" };
const thRowStyle = { backgroundColor: "#2c3e50", color: "#fff" };
const thStyle = { padding: "18px", textAlign: "left", fontWeight: "600" };
const tdStyle = { padding: "15px", borderBottom: "1px solid #eee" };
const trEven = { backgroundColor: "#fff" };
const trOdd = { backgroundColor: "#fcfcfc" };

const actionButton = { backgroundColor: "#3498db", color: "white", border: "none", padding: "8px 16px", borderRadius: "6px", cursor: "pointer", fontWeight: "600", transition: "0.3s" };
const greenButton = { backgroundColor: "#27ae60", color: "white", border: "none", padding: "10px 20px", borderRadius: "8px", cursor: "pointer", fontWeight: "bold" };
const blueButton = { backgroundColor: "#2980b9", color: "white", border: "none", padding: "10px 20px", borderRadius: "8px", cursor: "pointer", fontWeight: "bold" };
const redButton = { backgroundColor: "#e74c3c", color: "white", border: "none", padding: "10px 20px", borderRadius: "8px", cursor: "pointer", fontWeight: "bold" };

const modalOverlay = { position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 };
const modalContent = { backgroundColor: "#fff", padding: "30px", borderRadius: "15px", width: "500px", boxShadow: "0 10px 25px rgba(0,0,0,0.2)" };
const formStyle = { display: "flex", flexDirection: "column", gap: "12px" };
const inputGroup = { display: "flex", gap: "10px" };
const inputStyle = { padding: "10px", borderRadius: "6px", border: "1px solid #ddd", width: "100%" };
const textAreaStyle = { padding: "10px", borderRadius: "6px", border: "1px solid #ddd", width: "100%", minHeight: "60px", fontFamily: "inherit" };
const buttonGroup = { display: "flex", gap: "10px", marginTop: "15px" };

export default PatientList;